import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import EmptyState from './EmptyState';

interface Props {
  points: Array<{ date: string; drawdown: number }>;
  /** Fixed pixel height. Ignored when `fill` is true. */
  height?: number;
  /** When true, the chart fills its parent (use inside `flex-1` parents). */
  fill?: boolean;
}

const fmtInr = (n: number) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n));
const fmtInrSigned = (n: number) => (n >= 0 ? '+' : '-') + fmtInr(n);

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="font-semibold text-red-600 dark:text-red-400">
        Drawdown: {fmtInrSigned(val)}
      </p>
    </div>
  );
};

const DrawdownCard: React.FC<Props> = ({ points, height = 160, fill = false }) => {
  const data = useMemo(() => {
    const step = Math.max(1, Math.floor(points.length / 200));
    return points.filter((_, i) => i % step === 0 || i === points.length - 1);
  }, [points]);

  const axisStyle = { fontSize: 10, fill: '#9ca3af' };
  const gridStyle = 'rgba(156,163,175,0.15)';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden ${fill ? 'h-full flex flex-col' : ''}`}>
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Drawdown
        </p>
      </div>
      <div className={`px-5 pt-4 pb-2 ${fill ? 'flex-1 min-h-0' : ''}`}>
        {points.length === 0 ? (
          <EmptyState message="No drawdown data in this range." />
        ) : (
          <ResponsiveContainer width="100%" height={fill ? '100%' : height}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="ddGrad-overview" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStyle} />
              <XAxis dataKey="date" tick={axisStyle} tickLine={false} interval="preserveStartEnd" />
              <YAxis
                tick={axisStyle}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `₹${Math.abs(v) >= 1000 ? (-Math.abs(v) / 1000).toFixed(0) + 'k' : v}`}
                width={52}
              />
              <ReferenceLine y={0} stroke="rgba(156,163,175,0.4)" strokeDasharray="4 2" />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(156,163,175,0.3)', strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="drawdown"
                stroke="#ef4444"
                strokeWidth={2}
                fill="url(#ddGrad-overview)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default DrawdownCard;
